import { setupIonicReact } from '@ionic/react'
import { defineCustomElements } from '@ionic/pwa-elements/loader'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import { App } from './App'
import type { InstallEvent } from './lib/install'
import { INSTALLABLE_EVENT } from './lib/install'

/* Ionic's own stylesheets. Order matters: ours come last so a tenant's palette
   overrides the defaults rather than the other way round. */
import '@ionic/react/css/core.css'
import '@ionic/react/css/normalize.css'
import '@ionic/react/css/structure.css'
import '@ionic/react/css/typography.css'
import '@ionic/react/css/padding.css'
import '@ionic/react/css/flex-utils.css'
import '@ionic/react/css/display.css'

/*
 * Light only, for now. Ionic's system dark palette
 * (@ionic/react/css/palettes/dark.system.css) and our own theme/dark.css are
 * both left out on purpose; theme/dark.css says how to bring them back.
 */
import './theme/variables.css'
import './theme/blooq.css'

/*
 * `mode` is left to Ionic's own platform detection on purpose.
 *
 * Forcing `md` everywhere would make one app that looks the same on both
 * platforms and native on neither — iOS users read a Material back arrow as a
 * web page, and the ripple on a button confirms it. The whole reason for
 * choosing Ionic over hand-rolled components is that it already knows this.
 */
setupIonicReact({
  /*
   * The back gesture, in a browser and in an installed PWA.
   *
   * ionicframework.com/docs/developing/hardware-back-button — Ionic's back
   * button support is otherwise Capacitor-only, because a web page has no
   * hardware button to listen to. The Close Watcher API is the platform's
   * answer to that: it turns a close REQUEST — the Android back gesture in an
   * installed PWA, and the Escape key anywhere — into an event a page can
   * handle, and with this flag Ionic routes both through the same
   * `ionBackButton` it already uses natively.
   *
   * So this is what makes back mean "one step back in the booking" rather than
   * "leave the business's app", on the surface where most customers will open
   * it. See watchBackButton in lib/native.ts for the handler, and note that it
   * is now the ONLY transport — the Capacitor App plugin's own `backButton`
   * event is deliberately not listened to, so Ionic's overlay and menu
   * handlers get first refusal on every platform.
   *
   * `hardwareBackButton` is left alone: Ionic already defaults it to true
   * wherever this can work — a Capacitor shell, or a browser that has the API.
   * Chrome has had it since 120; anywhere else the event simply never fires and
   * back behaves as it always did.
   *
   * Experimental, in Ionic's own words. The failure mode if it is withdrawn is
   * that back leaves the page, which is where every non-Ionic web app already
   * is.
   */
  experimentalCloseWatcher: true,
})

/*
 * The web components behind Capacitor's plugins on the WEB.
 *
 * capacitorjs.com/docs/web/progressive-web-apps: a plugin like Camera has a
 * native implementation on a device and no UI of its own in a browser, so the
 * PWA build needs these elements registered or the call resolves into nothing.
 * The loader is lazy — nothing is fetched until a plugin actually asks for it.
 */
void defineCustomElements(window)

/*
 * Catch the install event before React exists.
 *
 * `beforeinstallprompt` fires during load, typically before the first render.
 * A listener registered inside a component misses it on a fast connection and
 * catches it on a slow one, which is why install buttons in most web apps
 * appear at random. Stashing it here — and re-broadcasting, for the case where
 * the component mounted first — makes it deterministic.
 *
 * preventDefault() suppresses Chrome's own mini-infobar so the business's own
 * ask is the only one; without it a customer gets two, which reads as a broken
 * page.
 */
window.addEventListener('beforeinstallprompt', (event) => {
  event.preventDefault()
  window.__blooqInstall = event as InstallEvent
  window.dispatchEvent(new Event(INSTALLABLE_EVENT))
})

const container = document.getElementById('root')
if (!container) throw new Error('#root is missing from index.html')

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
